import React, { useState, useEffect } from 'react';
import { getTask, updateTask, deleteTask, getTasks, addPredecessor, removePredecessor, updatePredecessor, getTaskAllocations } from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';

const DEP_TYPES = [
  { value: 'FS', label: 'FS — Finish to Start', short: 'FS' },
  { value: 'SS', label: 'SS — Start to Start', short: 'SS' },
  { value: 'FF', label: 'FF — Finish to Finish', short: 'FF' },
  { value: 'SF', label: 'SF — Start to Finish', short: 'SF' },
];

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getTaskTypeStyle = (type) => {
  const map = {
    Labor: { backgroundColor: '#dbeafe', color: '#1e40af' },
    Material: { backgroundColor: '#fef3c7', color: '#92400e' },
    Procurement: { backgroundColor: '#e0e7ff', color: '#3730a3' },
    Engineering: { backgroundColor: '#fce7f3', color: '#9d174d' },
    Inspection: { backgroundColor: '#ccfbf1', color: '#115e59' },
    Milestone: { backgroundColor: '#f3e8ff', color: '#6b21a8' },
  };
  return map[type] || { backgroundColor: '#f1f5f9', color: '#475569' };
};

const getStatusStyle = (status) => {
  const map = {
    'Not Started': { backgroundColor: '#f1f5f9', color: '#475569' },
    'In Progress': { backgroundColor: '#dbeafe', color: '#1e40af' },
    Complete: { backgroundColor: '#dcfce7', color: '#166534' },
  };
  return map[status] || map['Not Started'];
};

const TaskEditModal = ({ taskId, projectId, onClose, onSaved, onDeleted }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const t = useTheme();
  const [task, setTask] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [formData, setFormData] = useState({});
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showAddPred, setShowAddPred] = useState(false);
  const [predSearch, setPredSearch] = useState('');
  const [addPredType, setAddPredType] = useState('FS');
  const [addPredLag, setAddPredLag] = useState(0);
  const [addingPred, setAddingPred] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [taskData, tasksData, allocData] = await Promise.all([
          getTask(taskId),
          getTasks(projectId),
          getTaskAllocations(taskId).catch(() => []),
        ]);
        setTask(taskData);
        setAllTasks(tasksData);
        setAllocations(allocData);
        setFormData({
          task_name: taskData.task_name || '',
          task_description: taskData.task_description || '',
          building_area_phase: taskData.building_area_phase || '',
          task_type: taskData.task_type || '',
          start_date: taskData.start_date || '',
          finish_date: taskData.finish_date || '',
          duration_days: taskData.duration_days || 0,
          percent_complete: taskData.percent_complete || 0,
          status: taskData.status || 'Not Started',
          milestone_flag: taskData.milestone_flag || 0,
          responsible_party: taskData.responsible_party || '',
          notes: taskData.notes || '',
          client_visible: taskData.client_visible ?? 1,
          task_code: taskData.task_code || '',
        });
      } catch (err) {
        console.error('Error loading task:', err);
      } finally {
        setLoading(false);
      }
    };
    if (taskId) load();
  }, [taskId, projectId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const editDuration = (() => {
    if (formData.start_date && formData.finish_date) {
      const s = new Date(formData.start_date);
      const f = new Date(formData.finish_date);
      return Math.max(1, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
    }
    return formData.duration_days || 0;
  })();

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      let duration = editDuration;
      const saveData = {
        task_name: formData.task_name,
        task_description: formData.task_description,
        building_area_phase: formData.building_area_phase,
        task_type: formData.task_type,
        start_date: formData.start_date,
        finish_date: formData.finish_date,
        duration_days: duration,
        percent_complete: parseFloat(formData.percent_complete) || 0,
        status: formData.status,
        milestone_flag: formData.milestone_flag ? 1 : 0,
        responsible_party: formData.responsible_party,
        notes: formData.notes,
        client_visible: formData.client_visible ? 1 : 0,
      };
      await updateTask(taskId, saveData);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving task:', err);
      setSaveError(err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete Task',
      message: `Are you sure you want to delete "${formData.task_name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteTask(taskId);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const handlePredUpdate = async (predId, data) => {
    try {
      await updatePredecessor(taskId, predId, data);
      const updated = await getTask(taskId);
      setTask(updated);
    } catch (err) {
      console.error('Error updating predecessor:', err);
    }
  };

  const handleAddPredecessor = async (predecessorTaskId) => {
    setAddingPred(true);
    try {
      await addPredecessor(taskId, {
        predecessor_task_id: predecessorTaskId,
        dependency_type: addPredType,
        lag_days: addPredLag,
      });
      const updated = await getTask(taskId);
      setTask(updated);
      setShowAddPred(false);
      setPredSearch('');
      setAddPredType('FS');
      setAddPredLag(0);
    } catch (err) {
      console.error('Error adding predecessor:', err);
    } finally {
      setAddingPred(false);
    }
  };

  const handlePredRemove = async (predId) => {
    const ok = await confirm({
      title: 'Remove Predecessor',
      message: 'Remove this predecessor link?',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await removePredecessor(taskId, predId);
      const updated = await getTask(taskId);
      setTask(updated);
    } catch (err) {
      console.error('Error removing predecessor:', err);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: '13px',
    border: `1px solid ${t.inputBorder}`, borderRadius: '8px', outline: 'none',
    transition: 'border-color 0.2s', backgroundColor: t.inputBg, color: t.inputColor,
    boxSizing: 'border-box',
  };
  const inputDisabledStyle = { ...inputStyle, backgroundColor: t.bgAlt, color: t.textSecondary, cursor: 'default' };
  const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' };
  const sectionTitleStyle = { fontSize: '12px', fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${t.cardBorder}` };
  const fieldGroup = { marginBottom: '14px' };
  const focusStyle = (e) => e.target.style.borderColor = '#3b82f6';
  const blurStyle = (e) => e.target.style.borderColor = t.inputBorder;

  const pct = formData.percent_complete || 0;
  const predecessors = task?.predecessors || [];
  const successors = allTasks.filter(t =>
    (t.predecessors || []).some(p => Number(p.predecessor_task_id) === Number(taskId))
  );

  return (
    <>
      <div
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: t.modalBg, borderRadius: '16px', width: '580px', maxHeight: '88vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
          }}
        >
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: t.textSecondary }}>Loading task...</div>
          ) : (
            <>
              {/* Header */}
              <div style={{
                padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                borderBottom: `1px solid ${t.cardBorder}`,
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{
                      ...getTaskTypeStyle(formData.task_type),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                    }}>
                      {formData.task_type}
                    </span>
                    <span style={{ fontSize: '12px', color: t.textMuted, fontWeight: 500 }}>{formData.task_code}</span>
                    <span style={{
                      ...getStatusStyle(formData.status),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 500,
                    }}>
                      {formData.status || 'Not Started'}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: t.heading }}>
                    {formData.task_name || 'Task Properties'}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: t.textMuted, fontSize: '20px', lineHeight: 1, borderRadius: '6px',
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = t.bgAlt; e.target.style.color = t.text; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = t.textMuted; }}
                >
                  &times;
                </button>
              </div>

              {/* Scrollable body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

                {/* General section */}
                <div style={sectionTitleStyle}>General</div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Task Name</label>
                  <input type="text" name="task_name" value={formData.task_name} onChange={handleChange} style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Description</label>
                  <textarea name="task_description" value={formData.task_description} onChange={handleChange} rows="2" style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Building Area / Phase</label>
                    <input type="text" name="building_area_phase" value={formData.building_area_phase} onChange={handleChange} style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Task Type</label>
                    <select name="task_type" value={formData.task_type} onChange={handleChange} style={inputStyle}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Procurement</option>
                      <option>Engineering</option>
                      <option>Inspection</option>
                      <option>Milestone</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select name="status" value={formData.status || 'Not Started'} onChange={handleChange} style={inputStyle}>
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Responsible Party</label>
                    <input type="text" name="responsible_party" value={formData.responsible_party} onChange={handleChange} style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </div>
                </div>

                {/* Schedule section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Schedule</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Start Date</label>
                    <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Finish Date</label>
                    <input type="date" name="finish_date" value={formData.finish_date} onChange={handleChange} style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Duration</label>
                    <input type="text" value={`${editDuration} days`} disabled style={inputDisabledStyle} />
                  </div>
                </div>

                {/* Progress */}
                <div style={fieldGroup}>
                  <label style={labelStyle}>Completion</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ position: 'relative', flex: 1, height: '8px' }}>
                      <div style={{
                        position: 'absolute', inset: 0, backgroundColor: t.cardBorder,
                        borderRadius: '4px', overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`, height: '100%',
                          backgroundColor: pct >= 100 ? '#22c55e' : pct > 0 ? '#3b82f6' : t.textMuted,
                          borderRadius: '4px', transition: 'width 0.15s',
                        }} />
                      </div>
                      <input
                        type="range" min="0" max="100" step="1"
                        value={pct}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          let newStatus = formData.status;
                          if (val >= 100) newStatus = 'Complete';
                          else if (val > 0) newStatus = 'In Progress';
                          else newStatus = 'Not Started';
                          setFormData(prev => ({ ...prev, percent_complete: val, status: newStatus }));
                        }}
                        style={{
                          position: 'absolute', inset: 0, width: '100%', height: '100%',
                          opacity: 0, cursor: 'pointer', margin: 0,
                        }}
                      />
                    </div>
                    <input
                      type="number" min="0" max="100" step="1"
                      value={pct}
                      onChange={(e) => {
                        const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                        let newStatus = formData.status;
                        if (val >= 100) newStatus = 'Complete';
                        else if (val > 0) newStatus = 'In Progress';
                        else newStatus = 'Not Started';
                        setFormData(prev => ({ ...prev, percent_complete: val, status: newStatus }));
                      }}
                      style={{
                        width: '58px', padding: '4px 6px', fontSize: '13px', fontWeight: 600,
                        color: t.text, textAlign: 'center',
                        border: `1px solid ${t.inputBorder}`, borderRadius: '6px', outline: 'none',
                        backgroundColor: t.inputBg,
                      }}
                      onFocus={focusStyle} onBlur={blurStyle}
                    />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: t.textMuted }}>%</span>
                  </div>
                </div>

                {/* Predecessors */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Predecessors</div>
                {predecessors.length === 0 ? (
                  <div style={{ fontSize: '13px', color: t.textMuted, padding: '8px 0' }}>No predecessors linked</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0', ...fieldGroup }}>
                    {predecessors.map(pred => {
                      const predTask = allTasks.find(t => t.id === pred.predecessor_task_id);
                      return (
                        <div key={pred.id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '8px 10px', backgroundColor: t.bgAlt, borderRadius: '6px',
                          border: `1px solid ${t.cardBorder}`, fontSize: '13px',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div>
                              <span style={{ fontWeight: 500, color: t.text }}>{predTask?.task_code}</span>
                              <span style={{ color: t.textSecondary, marginLeft: '6px' }}>{predTask?.task_name}</span>
                            </div>
                            {predTask && (predTask.start_date || predTask.finish_date) && (
                              <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '2px' }}>
                                {predTask.start_date ? `Start: ${fmtDate(predTask.start_date)}` : ''}
                                {predTask.start_date && predTask.finish_date ? '  —  ' : ''}
                                {predTask.finish_date ? `Finish: ${fmtDate(predTask.finish_date)}` : ''}
                              </div>
                            )}
                          </div>
                          <select
                            value={pred.dependency_type || 'FS'}
                            onChange={(e) => handlePredUpdate(pred.id, { dependency_type: e.target.value, lag_days: pred.lag_days || 0 })}
                            style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.text }}
                          >
                            {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.short}</option>)}
                          </select>
                          <input
                            type="number"
                            value={pred.lag_days || 0}
                            onChange={(e) => handlePredUpdate(pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: parseInt(e.target.value) || 0 })}
                            style={{ width: '50px', padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: `1px solid ${t.cardBorder}`, textAlign: 'center', backgroundColor: t.bg, color: t.text }}
                            title="Lag days"
                          />
                          <button
                            onClick={() => handlePredRemove(pred.id)}
                            style={{
                              background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer',
                              fontSize: '16px', padding: '2px 6px', borderRadius: '4px',
                            }}
                            title="Remove predecessor"
                          >&times;</button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Predecessor */}
                {!showAddPred ? (
                  <button
                    onClick={() => setShowAddPred(true)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '6px 12px', fontSize: '12px', fontWeight: 600,
                      color: '#3b82f6', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                      borderRadius: '6px', cursor: 'pointer', marginBottom: '14px',
                    }}
                  >
                    + Add Predecessor
                  </button>
                ) : (
                  <div style={{
                    padding: '12px', backgroundColor: t.bgAlt, borderRadius: '8px',
                    border: `1px solid ${t.cardBorder}`, marginBottom: '14px',
                  }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <select
                        value={addPredType}
                        onChange={(e) => setAddPredType(e.target.value)}
                        style={{ padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.text }}
                      >
                        {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: t.textSecondary, fontWeight: 600 }}>Lag:</label>
                        <input
                          type="number"
                          value={addPredLag}
                          onChange={(e) => setAddPredLag(parseInt(e.target.value) || 0)}
                          style={{ width: '50px', padding: '6px', fontSize: '12px', borderRadius: '6px', border: `1px solid ${t.cardBorder}`, textAlign: 'center', backgroundColor: t.bg, color: t.text }}
                        />
                      </div>
                      <button
                        onClick={() => { setShowAddPred(false); setPredSearch(''); }}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer', fontSize: '14px', padding: '4px 8px' }}
                      >Cancel</button>
                    </div>
                    <input
                      type="text"
                      placeholder="Search tasks by name or code..."
                      value={predSearch}
                      onChange={(e) => setPredSearch(e.target.value)}
                      autoFocus
                      style={{ ...inputStyle, marginBottom: '6px', fontSize: '12px' }}
                    />
                    <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                      {(() => {
                        const existingPredIds = new Set(predecessors.map(p => p.predecessor_task_id));
                        const q = predSearch.toLowerCase().trim();
                        const candidates = allTasks.filter(ct =>
                          ct.id !== taskId &&
                          !existingPredIds.has(ct.id) &&
                          (q === '' || (ct.task_name || '').toLowerCase().includes(q) || (ct.task_code || '').toLowerCase().includes(q))
                        ).slice(0, 20);
                        if (candidates.length === 0) {
                          return <div style={{ fontSize: '12px', color: t.textMuted, padding: '8px' }}>No matching tasks found</div>;
                        }
                        return candidates.map(ct => (
                          <div
                            key={ct.id}
                            onClick={() => !addingPred && handleAddPredecessor(ct.id)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '6px 8px', borderRadius: '4px', cursor: addingPred ? 'wait' : 'pointer',
                              fontSize: '12px', color: t.text,
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bg}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div>
                              <span style={{ fontWeight: 600, color: t.heading }}>{ct.task_code}</span>
                              <span style={{ marginLeft: '6px', color: t.textSecondary }}>{ct.task_name}</span>
                            </div>
                            {ct.finish_date && (
                              <span style={{ fontSize: '11px', color: t.textMuted }}>{fmtDate(ct.finish_date)}</span>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* Dependent Tasks */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Dependent Tasks</div>
                {successors.length === 0 ? (
                  <div style={{ fontSize: '13px', color: t.textMuted, padding: '8px 0' }}>No tasks depend on this task</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0', ...fieldGroup }}>
                    {successors.map(s => {
                      const pred = (s.predecessors || []).find(p => Number(p.predecessor_task_id) === Number(taskId));
                      const depLabel = DEP_TYPES.find(d => d.value === pred?.dependency_type)?.short || pred?.dependency_type || 'FS';
                      const lagText = pred?.lag_days ? ` +${pred.lag_days}d` : '';
                      return (
                        <div key={s.id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 10px', backgroundColor: t.bgAlt, borderRadius: '6px',
                          border: `1px solid ${t.cardBorder}`, fontSize: '13px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                            <div style={{ minWidth: 0 }}>
                              <div>
                                <span style={{ fontWeight: 500, color: t.text }}>{s.task_code}</span>
                                <span style={{ color: t.textSecondary, marginLeft: '6px' }}>{s.task_name}</span>
                              </div>
                              {(s.start_date || s.finish_date) && (
                                <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '2px' }}>
                                  {s.start_date ? `Start: ${fmtDate(s.start_date)}` : ''}
                                  {s.start_date && s.finish_date ? '  —  ' : ''}
                                  {s.finish_date ? `Finish: ${fmtDate(s.finish_date)}` : ''}
                                </div>
                              )}
                            </div>
                          </div>
                          <span style={{
                            fontSize: '11px', fontWeight: 600, color: '#8b5cf6',
                            backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '10px',
                          }}>
                            {depLabel}{lagText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Options */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Options</div>
                <div style={{ display: 'flex', gap: '24px', ...fieldGroup }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="client_visible" checked={!!formData.client_visible} onChange={handleChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Client Visible
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="milestone_flag" checked={!!formData.milestone_flag} onChange={handleChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Milestone
                  </label>
                </div>

                {/* SOV Links */}
                {allocations.length > 0 && (
                  <>
                    <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>SOV Links</div>
                    <div style={fieldGroup}>
                      {allocations.map(a => {
                        const contractAmt = parseFloat(a.sov_contract_amount) || 0;
                        const allocPct = contractAmt > 0 ? Math.round((parseFloat(a.allocation_amount) / contractAmt) * 100) : 0;
                        return (
                          <div key={a.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
                            marginBottom: '6px', fontSize: '12px',
                          }}>
                            <div>
                              <span style={{ fontWeight: 600, color: t.text }}>{a.sov_code}</span>
                              <span style={{ color: t.textSecondary, marginLeft: '8px' }}>{a.sov_line_description}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600, color: t.text }}>
                                ${(a.allocation_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                              <span style={{
                                fontSize: '11px', fontWeight: 600, color: '#3b82f6',
                                backgroundColor: '#eff6ff', padding: '2px 8px', borderRadius: '10px',
                              }}>
                                {allocPct}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Notes */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Notes</div>
                <div style={fieldGroup}>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }} placeholder="Add notes about this task..." onFocus={focusStyle} onBlur={blurStyle} />
                </div>
              </div>

              {/* Footer */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '16px 24px', borderTop: `1px solid ${t.cardBorder}`,
                flexShrink: 0,
              }}>
                <button type="button" onClick={handleDelete}
                  style={{
                    background: 'none', border: '1px solid #fecaca', color: '#dc2626',
                    padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Delete
                </button>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={onClose}
                  style={{
                    background: t.bgAlt, border: 'none', color: t.text,
                    padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.cardBorder; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bgAlt; }}
                >
                  Cancel
                </button>
                <button type="button" onClick={handleSave} disabled={saving}
                  style={{
                    background: '#3b82f6', border: 'none', color: '#fff',
                    padding: '8px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer', opacity: saving ? 0.7 : 1,
                    boxShadow: '0 1px 3px rgba(59,130,246,0.3)',
                  }}
                  onMouseEnter={(e) => { if (!saving) e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              {saveError && (
                <div style={{
                  marginTop: '10px', padding: '10px 14px', borderRadius: '8px',
                  backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                  color: '#dc2626', fontSize: '13px', lineHeight: '1.4',
                }}>
                  {saveError}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};

export default TaskEditModal;
